import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Table as TableIcon, ArrowLeft, Plus, Pencil, Search, Power, PowerOff, ArrowUpDown } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const numeric = ["number", "decimal", "currency", "percentage"];

function MasterField({ fd, value, onChange, optionsMap }) {
  const t = fd.type;
  if (t === "textarea")
    return <Textarea data-testid={`master-field-${fd.key}`} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (t === "boolean")
    return <div className="pt-1"><Switch data-testid={`master-field-${fd.key}`} checked={!!value} onCheckedChange={onChange} /></div>;
  if (t === "single_select" || t === "dropdown")
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`master-field-${fd.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>{(fd.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  if (t === "multi_select")
    return (
      <div className="space-y-1.5 pt-1" data-testid={`master-field-${fd.key}`}>
        {(fd.options || []).map((o) => {
          const arr = Array.isArray(value) ? value : [];
          return (
            <label key={o} className="flex items-center gap-2 text-sm">
              <Checkbox checked={arr.includes(o)} onCheckedChange={(c) => onChange(c ? [...arr, o] : arr.filter((x) => x !== o))} />
              {o}
            </label>
          );
        })}
      </div>
    );
  if (t === "lookup" || t === "uom") {
    const opts = optionsMap[fd.key] || [];
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`master-field-${fd.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  const inputType = numeric.includes(t) ? "number" : t === "date" ? "date" : t === "datetime" ? "datetime-local" : "text";
  return <Input data-testid={`master-field-${fd.key}`} type={inputType} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

export default function MasterList() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = ["super_admin", "company_admin"].includes(user?.role);

  const [meta, setMeta] = useState({ label: "", fields: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [sort, setSort] = useState({ by: "name", dir: "asc" });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [optionsMap, setOptionsMap] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/master/${key}`).then((r) => setMeta(r.data)).catch((e) => toast.error(formatErr(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); setQ(""); setStatusF("all"); }, [key]);

  const listFields = useMemo(() => meta.fields.filter((f) => f.in_list), [meta.fields]);

  const rows = useMemo(() => {
    let r = [...(meta.rows || [])];
    if (statusF !== "all") r = r.filter((x) => x.status === statusF);
    if (q) {
      const s = q.toLowerCase();
      const searchable = meta.fields.filter((f) => f.searchable).map((f) => f.key);
      r = r.filter((x) => searchable.some((k) => String(x[k] ?? "").toLowerCase().includes(s)));
    }
    r.sort((a, b) => {
      const av = String(a[sort.by] ?? "").toLowerCase(), bv = String(b[sort.by] ?? "").toLowerCase();
      return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return r;
  }, [meta, q, statusF, sort]);

  const cellValue = (fd, row) => {
    const v = row[fd.key];
    if (fd.type === "lookup" || fd.type === "uom") return row[fd.key + "__label"] || v || "—";
    if (fd.type === "boolean") return v ? "Yes" : "No";
    if (v === null || v === undefined || v === "") return "—";
    if (fd.type === "currency") return `₹ ${v}`;
    if (fd.type === "percentage") return `${v} %`;
    return String(v);
  };

  const openForm = async (record) => {
    setEditing(record || null);
    const init = {};
    meta.fields.forEach((f) => { init[f.key] = record ? record[f.key] : (f.default ?? (f.type === "boolean" ? false : "")); });
    setForm(init);
    // fetch options for lookup/uom fields
    const map = {};
    await Promise.all(meta.fields.filter((f) => f.type === "lookup" || f.type === "uom").map(async (f) => {
      const ref = f.lookup || "uom";
      const params = f.uom_category ? `?category=${encodeURIComponent(f.uom_category)}` : "";
      try { const r = await api.get(`/master/${ref}/options${params}`); map[f.key] = r.data; } catch (e) { map[f.key] = []; }
    }));
    setOptionsMap(map);
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) await api.patch(`/master/${key}/${editing.id}`, form);
      else await api.post(`/master/${key}`, form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setSaving(false);
  };

  const toggleStatus = async (row) => {
    const next = row.status === "active" ? "inactive" : "active";
    try { await api.post(`/master/${key}/${row.id}/status`, { status: next }); toast.success(`Marked ${next}`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const setSortBy = (k) => setSort((s) => ({ by: k, dir: s.by === k && s.dir === "asc" ? "desc" : "asc" }));

  return (
    <AppLayout title={meta.label || "Master"} subtitle="Master Data · Reference records">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={() => navigate("/master")} data-testid="master-back-button">
          <ArrowLeft className="h-4 w-4 mr-1" /> All Masters
        </Button>
        <div className="flex-1" />
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md border border-slate-200 text-sm w-64">
          <Search className="h-3.5 w-3.5 text-slate-400" />
          <input placeholder="Search…" data-testid="master-search" value={q} onChange={(e) => setQ(e.target.value)}
            className="bg-transparent outline-none flex-1 text-slate-700 placeholder:text-slate-400" />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-36" data-testid="master-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        {canWrite && (
          <Button className="bg-sky-900 hover:bg-sky-950" onClick={() => openForm(null)} data-testid="master-add-button">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        )}
      </div>

      <Card className="overflow-x-auto" data-testid="master-table">
        <Table>
          <TableHeader>
            <TableRow>
              {listFields.map((f) => (
                <TableHead key={f.key} className="cursor-pointer select-none" onClick={() => setSortBy(f.key)} data-testid={`master-col-${f.key}`}>
                  <span className="inline-flex items-center gap-1">{f.label}<ArrowUpDown className="h-3 w-3 opacity-40" /></span>
                </TableHead>
              ))}
              <TableHead>Status</TableHead>
              {canWrite && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} data-testid={`master-row-${row.id}`}>
                {listFields.map((f) => (
                  <TableCell key={f.key} className={f.key === "name" ? "font-medium text-slate-800" : ""}>{cellValue(f, row)}</TableCell>
                ))}
                <TableCell>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${row.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`} data-testid={`master-status-${row.id}`}>
                    {row.status}
                  </span>
                </TableCell>
                {canWrite && (
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => openForm(row)} data-testid={`master-edit-${row.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleStatus(row)} data-testid={`master-toggle-${row.id}`} title={row.status === "active" ? "Deactivate" : "Activate"}>
                      {row.status === "active" ? <PowerOff className="h-3.5 w-3.5 text-red-500" /> : <Power className="h-3.5 w-3.5 text-emerald-600" />}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!loading && !rows.length && (
              <TableRow><TableCell colSpan={listFields.length + 2} className="text-center py-12 text-slate-400" data-testid="master-empty">No records</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="master-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {meta.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {meta.fields.filter((f) => !f.readonly).map((f) => (
              <div key={f.key}>
                <Label className="label-cap">{f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
                <MasterField fd={f} value={form[f.key]} onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} optionsMap={optionsMap} />
                {f.help && <div className="text-[11px] text-slate-400 mt-0.5">{f.help}</div>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={saving} className="bg-sky-900 hover:bg-sky-950" data-testid="master-save-button">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
